import React, { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Image as ImageIcon,
  Video,
  X,
  Upload,
  Loader2,
  Plus
} from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface MediaUploaderProps {
  mediaUrls: string[];
  mediaTypes: string[];
  onAdd: (url: string, type: 'image' | 'video') => void;
  onRemove: (index: number) => void;
  maxFiles?: number;
}

export const MediaUploader: React.FC<MediaUploaderProps> = ({
  mediaUrls,
  mediaTypes,
  onAdd,
  onRemove,
  maxFiles = 10
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const uploadFile = async (file: File): Promise<{ url: string; type: 'image' | 'video' } | null> => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      toast.error('Invalid file type', { description: 'Please upload an image or video file.' });
      return null;
    }

    // Check file size (max 50MB for videos, 10MB for images)
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large', {
        description: `Maximum file size is ${isVideo ? '50MB' : '10MB'}.`
      });
      return null;
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `social-media/${fileName}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('public')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed', { description: error.message });
      return null;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('public')
      .getPublicUrl(filePath);

    return {
      url: publicUrl,
      type: isVideo ? 'video' : 'image'
    };
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const remainingSlots = maxFiles - mediaUrls.length;

    if (fileArray.length > remainingSlots) {
      toast.error('Too many files', {
        description: `You can only add ${remainingSlots} more file${remainingSlots !== 1 ? 's' : ''}.`
      });
      return;
    }

    setIsUploading(true);

    try {
      for (const file of fileArray) {
        const result = await uploadFile(file);
        if (result) {
          onAdd(result.url, result.type);
        }
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [mediaUrls.length, maxFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const canAddMore = mediaUrls.length < maxFiles;

  return (
    <div className="space-y-3">
      {/* Media Preview Grid */}
      {mediaUrls.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {mediaUrls.map((url, index) => (
            <div
              key={index}
              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group"
            >
              {mediaTypes[index] === 'video' ? (
                <video
                  src={url}
                  className="w-full h-full object-cover"
                  muted
                />
              ) : (
                <img
                  src={url}
                  alt={`Media ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              )}

              {/* Type indicator */}
              <div className="absolute top-2 left-2">
                {mediaTypes[index] === 'video' ? (
                  <div className="bg-black/60 text-white rounded-full p-1">
                    <Video className="h-3 w-3" />
                  </div>
                ) : (
                  <div className="bg-black/60 text-white rounded-full p-1">
                    <ImageIcon className="h-3 w-3" />
                  </div>
                )}
              </div>

              {/* Remove button */}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}

          {/* Add more button */}
          {canAddMore && (
            <label
              className={cn(
                "aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors",
                "hover:border-primary hover:bg-primary/5"
              )}
            >
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileInput}
                className="hidden"
                disabled={isUploading}
              />
              {isUploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <Plus className="h-6 w-6 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground mt-1">Add more</span>
                </>
              )}
            </label>
          )}
        </div>
      )}

      {/* Upload Zone (shown when no media) */}
      {mediaUrls.length === 0 && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
            dragActive ? "border-primary bg-primary/5" : "border-border",
            isUploading && "opacity-50 pointer-events-none"
          )}
        >
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileInput}
            className="hidden"
            id="media-upload"
            disabled={isUploading}
          />
          <label
            htmlFor="media-upload"
            className="cursor-pointer flex flex-col items-center"
          >
            {isUploading ? (
              <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mb-3" />
            ) : (
              <Upload className="h-10 w-10 text-muted-foreground mb-3" />
            )}
            <p className="text-sm font-medium">
              {isUploading ? 'Uploading...' : 'Drop files here or click to upload'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Supports images and videos up to 50MB
            </p>
          </label>
        </div>
      )}

      {/* File count */}
      <p className="text-xs text-muted-foreground text-right">
        {mediaUrls.length}/{maxFiles} files
      </p>
    </div>
  );
};

export default MediaUploader;
